import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/contexts/OrganizationContext';
import type { Tag } from '@/types';

export type { Tag };

export interface TagsByGroup {
  [group: string]: Tag[];
}

// UI-only ordering for tag groups in filter sidebar
export const TAG_GROUP_ORDER = [
  "AI Experience",
  "Build Type",
  "Capabilities",
  "Tools",
  "Works of Mercy",
  "Cause",
  "Content Type"
];

// SPES-specific ordering for episcopal/diocesan focus
export const SPES_TAG_GROUP_ORDER = [
  "Ministry Area",
  "Audience",
  "Document Type",
  "Initiative",
  "Source"
];

// Special tags that should appear first in their group (before alphabetical sort)
const PRIORITY_TAGS: Record<string, string[]> = {
  "Content Type": ["Show & Tell"]
};

// Groups where AI is NOT allowed to auto-create tags
export const FIXED_TAG_GROUPS = [
  "AI Experience",
  "Build Type", 
  "Works of Mercy"
];

export const useTags = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsByGroup, setTagsByGroup] = useState<TagsByGroup>({});
  const [loading, setLoading] = useState(true);
  const { organization } = useOrganization();

  const loadTags = useCallback(async () => {
    setLoading(true);
    
    let query = supabase
      .from('tags')
      .select('*')
      .order('name');
    
    // Filter by organization if available
    if (organization?.id) {
      query = query.eq('organization_id', organization.id);
    }

    const { data, error } = await query;

    if (data && !error) {
      setTags(data as Tag[]);
      
      // Group tags by their group
      const grouped = data.reduce((acc: TagsByGroup, tag: Tag) => {
        if (!acc[tag.group]) {
          acc[tag.group] = [];
        }
        acc[tag.group].push(tag);
        return acc;
      }, {});
      
      // Sort tags within each group: priority tags first, then alphabetical
      for (const group of Object.keys(grouped)) {
        const priorityTags = PRIORITY_TAGS[group] || [];
        grouped[group].sort((a, b) => {
          const aIsPriority = priorityTags.includes(a.name);
          const bIsPriority = priorityTags.includes(b.name);
          
          if (aIsPriority && !bIsPriority) return -1;
          if (!aIsPriority && bIsPriority) return 1;
          return a.name.localeCompare(b.name);
        });
      }
      
      setTagsByGroup(grouped);
    }
    setLoading(false);
  }, [organization?.id]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // Get or create a tag by group and name
  const getOrCreateTag = async (group: string, name: string): Promise<Tag | null> => {
    if (!organization?.id) return null;
    
    // First, try to find existing tag within this organization
    const { data: existing } = await supabase
      .from('tags')
      .select('*')
      .eq('group', group)
      .eq('name', name)
      .eq('organization_id', organization.id)
      .maybeSingle();

    if (existing) {
      return existing as Tag;
    }

    // Create new tag with organization_id
    const { data: newTag, error } = await supabase
      .from('tags')
      .insert([{ group, name, organization_id: organization.id }])
      .select()
      .single();

    if (error) {
      console.error('Error creating tag:', error);
      return null;
    }

    // Refresh tags list
    await loadTags();
    return newTag as Tag;
  };

  // Attach tags to a resource
  const attachTagsToResource = async (resourceId: string, tagIds: string[]): Promise<boolean> => {
    // First, remove existing tags for this resource
    await supabase
      .from('resource_tags')
      .delete()
      .eq('resource_id', resourceId);

    if (tagIds.length === 0) return true;

    // Insert new tag associations
    const { error } = await supabase
      .from('resource_tags')
      .insert(
        tagIds.map(tagId => ({
          resource_id: resourceId,
          tag_id: tagId
        }))
      );

    if (error) {
      console.error('Error attaching tags:', error);
      return false;
    }

    return true;
  };

  // Get tags for a specific resource
  const getResourceTags = async (resourceId: string): Promise<Tag[]> => {
    const { data, error } = await supabase
      .from('resource_tags')
      .select('tag_id, tags(*)')
      .eq('resource_id', resourceId);

    if (error || !data) return [];
    
    return data.map((rt: any) => rt.tags as Tag).filter(Boolean);
  };

  // Get tags for multiple resources
  const getResourcesWithTags = async (resourceIds: string[]): Promise<Map<string, Tag[]>> => {
    if (resourceIds.length === 0) return new Map();

    const { data, error } = await supabase
      .from('resource_tags')
      .select('resource_id, tag_id, tags(*)')
      .in('resource_id', resourceIds);

    if (error || !data) return new Map();

    const result = new Map<string, Tag[]>();
    for (const rt of data) {
      const existing = result.get(rt.resource_id) || [];
      if (rt.tags) {
        existing.push(rt.tags as Tag);
      }
      result.set(rt.resource_id, existing);
    }

    return result;
  };

  // Get ordered groups for UI display
  const getOrderedGroups = (): string[] => {
    const allGroups = Object.keys(tagsByGroup);
    // Return groups in defined order, then any remaining groups alphabetically
    const orderedGroups = TAG_GROUP_ORDER.filter(g => allGroups.includes(g));
    const remainingGroups = allGroups.filter(g => !TAG_GROUP_ORDER.includes(g)).sort();
    return [...orderedGroups, ...remainingGroups];
  };

  // Check if a resource has a specific tag
  const hasTag = (resourceTags: Tag[] | undefined, tagName: string): boolean => {
    return resourceTags?.some(t => t.name === tagName) || false;
  };

  return {
    tags,
    tagsByGroup,
    loading,
    loadTags,
    getOrCreateTag,
    attachTagsToResource,
    getResourceTags,
    getResourcesWithTags,
    getOrderedGroups,
    hasTag
  };
};
